/**
 * Every API path the app knows, in one place.
 *
 * Paths are relative to `appConfig.apiPrefix` (/api/v1). Services import from
 * here rather than writing URL strings inline, so a backend route change is
 * one edit and `grep endpointConfig` answers "what does the SPA call?".
 */
import type { CalendarProvider } from '@/@types/trackheros'

const endpointConfig = {
    // User-scoped, not nested under a client: the dashboard aggregates across
    // every client the user owns (the one legitimately cross-client read —
    // see backend apps/meetings/dashboard.py).
    dashboardStats: '/dashboard/stats/',

    // Also user-scoped. The Meetings page is one developer's own calendar
    // across their own clients — the product's whole reason to exist — so the
    // client is an optional *filter* here rather than a path segment. The
    // server still derives the reachable set from the authenticated user and
    // verifies any id it is given (backend apps/meetings/unified.py).
    unifiedMeetings: '/meetings/',

    // Also user-scoped, same shape and same reason: one developer's own
    // commitments across their own clients, with the client as a
    // server-verified filter rather than a claim the caller asserts.
    myActionItems: '/action-items/',

    // Global search: one box over clients, meetings, action items, summaries
    // and transcripts. Same shape again — the client is an optional
    // server-verified filter, and the default scope is every client the user
    // owns, which is the point of having one box (backend apps/search/).
    globalSearch: '/search/',

    // Exchanges a Firebase ID token — Google, Microsoft or email/password — for
    // our own JWT pair. The one door in; everything past it is unchanged and
    // does not know Firebase exists (backend apps/accounts/views.py).
    authFirebase: '/auth/firebase',
    // One-time password migration for accounts that predate Firebase. Answers
    // 204 whatever happened, deliberately — see the backend view.
    authFirebaseMigrate: '/auth/firebase/migrate',
    passwordReset: '/auth/password-reset',
    passwordChanged: '/auth/password-changed',
    verifyEmailResend: '/auth/verify-email/resend',

    signOut: '/auth/sign-out',
    refresh: '/auth/refresh',
    // Two-factor (backend apps/accounts/twofactor.py). `verify` is the second
    // half of a sign-in and unauthenticated; the rest are Settings → Security
    // and signed in.
    twoFactorVerify: '/auth/two-factor/verify',
    twoFactor: '/auth/two-factor',
    twoFactorSetup: '/auth/two-factor/setup',
    twoFactorEnable: '/auth/two-factor/enable',
    twoFactorDisable: '/auth/two-factor/disable',
    twoFactorRecoveryCodes: '/auth/two-factor/recovery-codes',
    sessionsRevokeAll: '/auth/sessions/revoke-all',
    me: '/auth/me',

    // Pre-Firebase. Still answering while old mobile builds are in the wild;
    // the SPA no longer calls them.
    signIn: '/auth/sign-in',
    signUp: '/auth/sign-up',
    // Sign in *with* Google. Not to be confused with `connectGoogle` below,
    // which authorises a client's calendar — different scopes, different
    // consent screen, different table (backend apps/accounts/oauth.py).
    oauthGoogle: '/auth/oauth/google',

    // Clients are the isolation boundary, so meeting paths are nested under a
    // client id rather than taking it as a filter — the server verifies the id
    // before any query runs (CLAUDE.md §2.1).
    clients: '/clients/',
    client: (clientId: string) => `/clients/${clientId}/`,
    meetings: (clientId: string) => `/clients/${clientId}/meetings/`,
    meeting: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/`,
    meetingTranscript: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/transcript/`,
    meetingRedispatch: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/redispatch/`,

    // Take the bot out of a meeting happening now. The counterpart to
    // auto-join rather than to redispatch: auto-join decides whether a bot goes
    // out at all, this ends a visit already in progress.
    meetingLeave: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/leave/`,

    // A <video> element cannot send an Authorization header, so the media URL
    // carries a short-lived signed token instead. It authenticates only — the
    // server still resolves ownership from the database on every request.
    meetingRecording: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/recording/`,
    meetingPlaybackToken: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/recording/playback-token/`,

    meetingAutoJoin: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/auto-join/`,

    // The minutes language for one meeting or its whole series — the
    // auto-join endpoint's shape. The catalogue is served, never copied here.
    meetingMinutesLanguage: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/minutes-language/`,
    minutesLanguages: '/minutes-languages/',

    meetingActionItem: (clientId: string, meetingId: string, itemId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/action-items/${itemId}/`,

    // A person editing the summary, and choosing between their edits and a
    // newer model version written beside them.
    meetingSummary: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/summary/`,
    meetingSummaryRevision: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/summary/revision/`,

    // The meeting checklist — what to raise before this call ends. `scope` on
    // the write verbs is what makes a recurring series one decision rather
    // than one per occurrence.
    meetingChecklist: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/checklist/`,
    meetingChecklistItem: (
        clientId: string,
        meetingId: string,
        itemId: string,
    ) => `/clients/${clientId}/meetings/${meetingId}/checklist/${itemId}/`,
    meetingChecklistCarry: (
        clientId: string,
        meetingId: string,
        itemId: string,
    ) =>
        `/clients/${clientId}/meetings/${meetingId}/checklist/${itemId}/carry-forward/`,

    // Not an API path: the WebSocket the checklist panel opens. Served by a
    // separate ASGI process, so it is NOT under /api/v1 — see
    // services/MeetingLiveSocket.ts for how the absolute ws:// URL is built.
    meetingLiveSocket: (clientId: string, meetingId: string) =>
        `/ws/clients/${clientId}/meetings/${meetingId}/`,

    // Regenerate the summary from the stored transcript, so a new correction
    // rule reaches a meeting summarised before the rule existed (DEV-136).
    meetingResummarise: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/resummarise/`,

    // Notebooks: the per-client record the user builds up by topic. The
    // unnested path is the cross-client listing page — names and counts only,
    // every row labelled with its client; contents stay behind the client path.
    myNotebooks: '/notebooks/',
    notebooks: (clientId: string) => `/clients/${clientId}/notebooks/`,
    notebook: (clientId: string, notebookId: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/`,
    notebookEntries: (clientId: string, notebookId: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/entries/`,
    notebookEntry: (clientId: string, notebookId: string, entryId: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/entries/${entryId}/`,
    // The two actions that spend money on request; each answers 429 inside
    // its cooldown rather than queueing a second run.
    notebookRecheck: (clientId: string, notebookId: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/recheck/`,
    notebookRebuildState: (clientId: string, notebookId: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/rebuild-state/`,

    // The rules live under the meeting, not the notebook: they are a decision
    // about this meeting, and `scope` on the write verbs is what makes a
    // recurring series one decision rather than one per occurrence.
    meetingNotebookRules: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/notebook-rules/`,
    meetingNotebookRule: (
        clientId: string,
        meetingId: string,
        ruleId: string,
    ) => `/clients/${clientId}/meetings/${meetingId}/notebook-rules/${ruleId}/`,
    meetingNotebookRulesRun: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/notebook-rules/run/`,

    // Correction rules: per-client transcription fixes injected into future
    // transcription and summary prompts (DEV-136).
    clientRules: (clientId: string) => `/clients/${clientId}/rules/`,
    clientRule: (clientId: string, ruleId: string) =>
        `/clients/${clientId}/rules/${ruleId}/`,

    // The assistant (DEV-128/129/139). Two conversation surfaces: per-client
    // chats live under the verified client path segment; `/chats/` is the
    // user-scoped surface — the one labeled list feeding Messaging, plus the
    // global-mode conversation lifecycle. The messages endpoints stream SSE.
    chats: '/chats/',
    chat: (chatId: string) => `/chats/${chatId}/`,
    chatMessages: (chatId: string) => `/chats/${chatId}/messages/`,
    chatApprove: (chatId: string, messageId: string) =>
        `/chats/${chatId}/messages/${messageId}/approve/`,
    // Dictation: a recording in, the text for the composer out. Not nested
    // under a conversation — the composer offers the microphone before the
    // first message exists — and never a message: what comes back is a draft
    // the user edits and sends themselves.
    chatVoice: '/chats/voice/',
    clientChatVoice: (clientId: string) => `/clients/${clientId}/chats/voice/`,
    clientChats: (clientId: string) => `/clients/${clientId}/chats/`,
    clientChat: (clientId: string, chatId: string) =>
        `/clients/${clientId}/chats/${chatId}/`,
    clientChatMessages: (clientId: string, chatId: string) =>
        `/clients/${clientId}/chats/${chatId}/messages/`,
    // Taking one answer out of the app (DEV-142). `export/` is every rendering
    // the copy actions need in one call; `export.pdf` is the file, fetched as
    // an authorised blob rather than a shareable URL — an answer quotes client
    // meetings, so a link anyone could open is a leak.
    chatExport: (chatId: string, messageId: string) =>
        `/chats/${chatId}/messages/${messageId}/export/`,
    chatExportPdf: (chatId: string, messageId: string) =>
        `/chats/${chatId}/messages/${messageId}/export.pdf`,
    clientChatExport: (clientId: string, chatId: string, messageId: string) =>
        `/clients/${clientId}/chats/${chatId}/messages/${messageId}/export/`,
    clientChatExportPdf: (
        clientId: string,
        chatId: string,
        messageId: string,
    ) =>
        `/clients/${clientId}/chats/${chatId}/messages/${messageId}/export.pdf`,
    clientChatApprove: (clientId: string, chatId: string, messageId: string) =>
        `/clients/${clientId}/chats/${chatId}/messages/${messageId}/approve/`,
    // Reversing a turn's undoable edits. Client-scoped only: global
    // conversations are read-only and produce nothing to undo.
    clientChatUndo: (clientId: string, chatId: string, messageId: string) =>
        `/clients/${clientId}/chats/${chatId}/messages/${messageId}/undo/`,

    // Minutes of meeting. `minutes/` returns every rendering the export dialog
    // needs at once; `minutes.pdf` is the file, fetched as an authorised blob
    // rather than a shareable URL — this document is client-facing.
    meetingMinutes: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/minutes/`,
    meetingMinutesPdf: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/minutes.pdf`,
    minutesTemplate: (clientId: string) =>
        `/clients/${clientId}/minutes-template/`,
    preferences: '/auth/preferences',

    // --- Booking links (backend apps/booking) ---
    // The host's own objects: profile, schedules, links, the calendar picker,
    // and their bookings across every client (the eighth audited cross-client
    // read — the client is a server-verified filter, as on /meetings/).
    bookingProfile: '/me/booking/profile/',
    bookingAccounts: '/me/booking/accounts/',
    bookingSchedules: '/me/booking/schedules/',
    bookingSchedule: (id: string) => `/me/booking/schedules/${id}/`,
    bookingLinks: '/me/booking/links/',
    bookingLink: (id: string) => `/me/booking/links/${id}/`,
    bookingLinkPreviewSlots: (id: string) =>
        `/me/booking/links/${id}/preview-slots/`,
    bookingBookings: '/me/booking/bookings/',
    bookingCancel: (id: string) => `/me/booking/bookings/${id}/cancel/`,
    bookingRequestReschedule: (id: string) =>
        `/me/booking/bookings/${id}/request-reschedule/`,
    // Calendar *write* access, asked for on the one account a link creates
    // its appointments in. The same round trip as a reconnect.
    upgradeCalendar: (
        clientId: string,
        provider: CalendarProvider,
        accountId: string,
    ) => `/clients/${clientId}/connect/${provider}/${accountId}/upgrade/`,
    upgradePersonalCalendar: (provider: CalendarProvider, accountId: string) =>
        `/me/connect/${provider}/${accountId}/upgrade/`,
    // The public booking pages. Unauthenticated; the server throttles per IP.
    publicBookingProfile: (handle: string) => `/book/${handle}/`,
    publicBookingLink: (handle: string, slug: string) =>
        `/book/${handle}/${slug}/`,
    publicBookingSlots: (handle: string, slug: string) =>
        `/book/${handle}/${slug}/slots/`,
    bookingManage: (token: string) => `/bookings/manage/${token}/`,
    bookingManageCancel: (token: string) => `/bookings/manage/${token}/cancel/`,
    bookingManageReschedule: (token: string) =>
        `/bookings/manage/${token}/reschedule/`,

    // Hard delete is a POST carrying a typed confirmation, not a DELETE:
    // `DELETE /clients/<id>/` archives, which is the reversible action and the
    // one people reach for by accident.
    hardDeleteClient: (clientId: string) => `/clients/${clientId}/delete/`,
    hardDeleteAccount: '/account/delete/',

    // The developer's own Zoom account. Under `me/` rather than under a client
    // because one Zoom login attends every client's meetings.
    zoomIdentities: '/me/zoom/',
    zoomStart: '/me/zoom/start/',
    zoomDisconnect: (identityId: string) => `/me/zoom/${identityId}/`,

    // Desktop recorder devices. Under `auth/` rather than `me/` because the
    // three endpoints the *app* uses live beside them and are unauthenticated —
    // the app has no session until one of them gives it one. Only these four
    // are reachable from the browser; the app's own three are never called here.
    devices: '/auth/devices',
    devicePending: '/auth/devices/pending',
    deviceApprove: '/auth/devices/approve',
    deviceRevoke: (deviceId: string) => `/auth/devices/${deviceId}/revoke`,
    // Alerts (Account → Alerts). Per person like devices, so under `auth/`.
    alerts: '/auth/alerts',
    alertsReadAll: '/auth/alerts/read-all',
    alertRead: (alertId: string) => `/auth/alerts/${alertId}/read`,

    // The MCP server — how Claude, ChatGPT and similar read this account.
    //
    // Only these five are ours to call. The OAuth flow an AI client drives
    // (/authorize, /token, /register, /revoke and the .well-known documents) is
    // served by a different process on the same host and is never touched from
    // the browser; the browser's part is the consent decision and the management
    // page. Note these are unversioned-looking but do sit under /api/v1 like
    // everything else here.
    mcpConsent: (requestId: string) => `/mcp/consent/${requestId}`,
    mcpConsentApprove: (requestId: string) =>
        `/mcp/consent/${requestId}/approve`,
    mcpConsentDeny: (requestId: string) => `/mcp/consent/${requestId}/deny`,
    mcpConnections: '/mcp/connections',
    mcpConnectionRevoke: (connectionId: string) =>
        `/mcp/connections/${connectionId}/revoke`,

    accounts: (clientId: string) => `/clients/${clientId}/accounts/`,
    // Connecting a client's calendar, per provider. Not sign-in: the account
    // is attached to one client and its events become that client's meetings.
    connectCalendar: (clientId: string, provider: CalendarProvider) =>
        `/clients/${clientId}/connect/${provider}/`,
    disconnectCalendar: (
        clientId: string,
        provider: CalendarProvider,
        accountId: string,
    ) => `/clients/${clientId}/connect/${provider}/${accountId}/`,

    // Connectors: the developer's own Jira/Linear/Notion/Slack/Gmail/Drive, one
    // grant per client.
    connectors: (clientId: string) => `/clients/${clientId}/connectors/`,
    connectorStart: (clientId: string, provider: string) =>
        `/clients/${clientId}/connectors/${provider}/start/`,
    connectorDisconnect: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/`,
    connectorProjects: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/projects/`,
    slackSettings: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/slack-settings/`,
    driveSettings: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/drive-settings/`,
    drivePickerToken: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/picker-token/`,
    notionSettings: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/notion-settings/`,
    notionSearch: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/notion-search/`,
    notionDocuments: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/notion-documents/`,
    notionDocument: (
        clientId: string,
        connectorId: string,
        documentId: string,
    ) =>
        `/clients/${clientId}/connectors/${connectorId}/notion-documents/${documentId}/`,
    meetingNotionExport: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/notion-export/`,
    notebookNotionMirror: (clientId: string, notebookId: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/notion-mirror/`,
    notebookNotionMirrorResume: (clientId: string, notebookId: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/notion-mirror/resume/`,
    actionItemTicket: (clientId: string, meetingId: string, itemId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/action-items/${itemId}/ticket/`,

    // Destinations: where this client's finished meetings are sent. Client-
    // scoped like everything else here, and addressed by `kind` rather than by
    // row id — there is exactly one config per (client, kind), so a uuid would
    // be a second thing to look up before you could configure the first.
    destinations: (clientId: string) => `/clients/${clientId}/destinations/`,
    destination: (clientId: string, kind: string) =>
        `/clients/${clientId}/destinations/${kind}/`,
    destinationSecret: (clientId: string, kind: string) =>
        `/clients/${clientId}/destinations/${kind}/secret/`,
    destinationTest: (clientId: string, kind: string) =>
        `/clients/${clientId}/destinations/${kind}/test/`,
    deliveries: (clientId: string) => `/clients/${clientId}/deliveries/`,
    deliveryRetry: (clientId: string, deliveryId: string) =>
        `/clients/${clientId}/deliveries/${deliveryId}/retry/`,

    connectorSite: (clientId: string, connectorId: string) =>
        `/clients/${clientId}/connectors/${connectorId}/site/`,

    ticketReferences: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/ticket-references/`,

    // The client's ticket cache: every tracker ticket their meetings have
    // mentioned, with which meetings mentioned it.
    clientTickets: (clientId: string) => `/clients/${clientId}/tickets/`,
    clientTicket: (clientId: string, ticketId: string) =>
        `/clients/${clientId}/tickets/${ticketId}/`,
    clientTicketRefresh: (clientId: string, ticketId: string) =>
        `/clients/${clientId}/tickets/${ticketId}/refresh/`,
    proposedActions: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/proposed-actions/`,
    generateProposedActions: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/proposed-actions/generate/`,
    proposedAction: (clientId: string, meetingId: string, actionId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/proposed-actions/${actionId}/`,
    executeProposedAction: (
        clientId: string,
        meetingId: string,
        actionId: string,
    ) =>
        `/clients/${clientId}/meetings/${meetingId}/proposed-actions/${actionId}/execute/`,

    // Billing (DEV-87). User-scoped, not client-scoped: a developer owns many
    // clients and pays one bill, so there is no client segment anywhere here.
    // `summary` is one call on purpose — plan, all three meters, card and
    // warnings — the same shape as dashboardStats, because three round trips to
    // render one page is three chances for a partial render.
    billingSummary: '/billing/summary/',
    billingPlans: '/billing/plans/',
    billingSubscribe: '/billing/subscribe/',
    billingPaymentSetup: '/billing/payment-method/setup/',
    billingPaymentMethod: '/billing/payment-method/',
    billingInvoices: '/billing/invoices/',
    // Paddle only. The portal links and invoice PDFs are short-lived, so both
    // are minted per click rather than carried in a list.
    billingPortal: '/billing/portal/',
    billingInvoiceDownload: (invoiceId: string) =>
        `/billing/invoices/${encodeURIComponent(invoiceId)}/download/`,
    // Plans v2. `features` is the one billing read a member may make: what the
    // plan includes, never what the account has used or spent.
    billingFeatures: '/billing/features/',
    billingStorageSettings: '/billing/storage-settings/',
    billingAddonHours: '/billing/addons/hours/',
    billingAddonStorage: '/billing/addons/storage/',
    // The recordings taking the account's storage, across its clients — the
    // seventh audited cross-client read. Metadata only; deleting takes the
    // media and never the transcript or notes.
    recordings: '/recordings/',
    recordingsDelete: '/recordings/delete/',
    recordingDetail: (meetingId: string) => `/recordings/${meetingId}/`,

    // The organisation (DEV-16x). Not nested under an id: there is exactly one
    // per user, resolved from their membership server-side, so an id in the
    // path would be a caller-supplied filter on the thing the membership
    // already decides.
    org: '/org/',
    orgConvert: '/org/convert/',
    orgSuspend: '/org/suspend/',
    orgReactivate: '/org/reactivate/',
    orgTransfer: '/org/transfer-ownership/',
    orgMembers: '/org/members/',
    orgMember: (id: string) => `/org/members/${id}/`,
    orgInvitations: '/org/invitations/',
    orgInvitation: (id: string) => `/org/invitations/${id}/`,
    // The accept flow sits outside `/org/` because the caller is not in an
    // organisation yet — that is the point of the link.
    invitation: (token: string) => `/invitations/${token}/`,
    invitationAccept: (token: string) => `/invitations/${token}/accept/`,
    // A member's own calendar. Under `me/` rather than under a client,
    // because its events do not belong to one yet — they land in the caller's
    // private bucket and are routed from there.
    // The list is the same on either provider's path; the POST starts that
    // provider's consent flow.
    personalCalendar: (provider: CalendarProvider = 'google') =>
        `/me/connect/${provider}/`,
    personalCalendarAccount: (provider: CalendarProvider, id: string) =>
        `/me/connect/${provider}/${id}/`,
    // Filing a meeting under a client. A write on the *source* client's path
    // with the destination in the body, because the caller has to be able to
    // reach both.
    assignMeeting: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/assign/`,
    // Sharing a notebook. Under the notebook rather than the client: who may
    // read one notebook is its creator's decision, not a client-level setting.
    notebookMembers: (clientId: string, notebookId: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/members/`,
    notebookMember: (clientId: string, notebookId: string, id: string) =>
        `/clients/${clientId}/notebooks/${notebookId}/members/${id}/`,

    // The knowledge graph. Nested under the client, the isolation boundary;
    // there is deliberately no cross-client entities list (backend
    // apps/graph/urls.py).
    entities: (clientId: string) => `/clients/${clientId}/entities/`,
    entity: (clientId: string, entityId: string) =>
        `/clients/${clientId}/entities/${entityId}/`,
    entityTimeline: (clientId: string, entityId: string) =>
        `/clients/${clientId}/entities/${entityId}/timeline/`,
    entityMerge: (clientId: string, entityId: string) =>
        `/clients/${clientId}/entities/${entityId}/merge/`,
    entitySplit: (clientId: string, entityId: string) =>
        `/clients/${clientId}/entities/${entityId}/split/`,
    entityDistinguish: (clientId: string, entityId: string) =>
        `/clients/${clientId}/entities/${entityId}/distinguish/`,
    entityHide: (clientId: string, entityId: string) =>
        `/clients/${clientId}/entities/${entityId}/hide/`,
    entityUnhide: (clientId: string, entityId: string) =>
        `/clients/${clientId}/entities/${entityId}/unhide/`,
    meetingEntities: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/entities/`,

    // Trackers: tables the user defines, kept current by their meetings
    // (backend apps/trackers). Nested under the client, the isolation
    // boundary; every tracker also lives in a notebook, which decides who sees
    // it. Off (404) while TRACKERS_ENABLED is off.
    trackerTemplates: '/tracker-templates/',
    trackers: (clientId: string) => `/clients/${clientId}/trackers/`,
    trackerSeries: (clientId: string) =>
        `/clients/${clientId}/trackers/series/`,
    trackerDraft: (clientId: string) => `/clients/${clientId}/trackers/draft/`,
    trackerImprove: (clientId: string) =>
        `/clients/${clientId}/trackers/draft/improve/`,
    trackerSuggest: (clientId: string) =>
        `/clients/${clientId}/trackers/draft/suggest/`,
    trackerPreview: (clientId: string) =>
        `/clients/${clientId}/trackers/preview/`,
    tracker: (clientId: string, trackerId: string) =>
        `/clients/${clientId}/trackers/${trackerId}/`,
    trackerAction: (clientId: string, trackerId: string, action: string) =>
        `/clients/${clientId}/trackers/${trackerId}/${action}/`,
    trackerRows: (clientId: string, trackerId: string) =>
        `/clients/${clientId}/trackers/${trackerId}/rows/`,
    trackerRow: (clientId: string, trackerId: string, rowId: string) =>
        `/clients/${clientId}/trackers/${trackerId}/rows/${rowId}/`,
    trackerRowAction: (
        clientId: string,
        trackerId: string,
        rowId: string,
        action: string,
    ) => `/clients/${clientId}/trackers/${trackerId}/rows/${rowId}/${action}/`,
    trackerCell: (
        clientId: string,
        trackerId: string,
        rowId: string,
        fieldId: string,
    ) =>
        `/clients/${clientId}/trackers/${trackerId}/rows/${rowId}/cells/${fieldId}/`,
    trackerVersionAction: (
        clientId: string,
        trackerId: string,
        versionId: string,
        action: string,
    ) =>
        `/clients/${clientId}/trackers/${trackerId}/versions/${versionId}/${action}/`,
    trackerProposals: (clientId: string, trackerId: string) =>
        `/clients/${clientId}/trackers/${trackerId}/proposals/`,
    trackerProposalAction: (
        clientId: string,
        trackerId: string,
        proposalId: string,
        action: string,
    ) =>
        `/clients/${clientId}/trackers/${trackerId}/proposals/${proposalId}/${action}/`,
    trackerMeetingAction: (
        clientId: string,
        trackerId: string,
        meetingId: string,
        action: string,
    ) =>
        `/clients/${clientId}/trackers/${trackerId}/meetings/${meetingId}/${action}/`,
    trackerExport: (
        clientId: string,
        trackerId: string,
        kind: 'export' | 'history',
    ) => `/clients/${clientId}/trackers/${trackerId}/${kind}.csv`,
    meetingTrackerChanges: (clientId: string, meetingId: string) =>
        `/clients/${clientId}/meetings/${meetingId}/tracker-changes/`,
}

export default endpointConfig
